import React, { useMemo } from "react";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import { formatHelper } from "helper/format";

const FALLBACK_BENEFITS = [
  "AI Powered Analysis",
  "Portfolio Health Report",
  "Portfolio Highlights",
  "Key Insights",
  "Personalized Recommendations",
  "Risk & Allocation Review",
  "Diversification Analysis",
  "Quick Report",
];

const CARD_GRADIENT = "linear-gradient(162deg, #ED6767 2%, #221919 98%)";
const CTA_TEXT = "#E36363";
const BADGE_TEXT = "#5A2626";

const resolvePlan = (plans = []) => {
  const fromApi = Array.isArray(plans) ? plans.filter(Boolean) : [];
  const apiPlan = fromApi[0] || null;
  const checklist = Array.isArray(apiPlan?.planChecklist)
    ? apiPlan.planChecklist.filter(Boolean)
    : [];
  const planPrice =
    apiPlan?.planPrice == null || apiPlan?.planPrice === ""
      ? 0
      : Number(apiPlan.planPrice);

  return {
    planPrice: Number.isFinite(planPrice) ? planPrice : 0,
    benefits: checklist.length ? checklist : FALLBACK_BENEFITS,
    badgeLabel: planPrice > 0 ? "Subscription" : "No Subscription Required",
    ctaLabel: planPrice > 0 ? "Unlock Portfolio Check" : "Free Portfolio Check",
  };
};

const formatPriceLabel = (planPrice) => {
  if (!planPrice) return "Free";
  return formatHelper.currency(planPrice, {
    config: { maximumFractionDigits: 0, minimumFractionDigits: 0 },
  });
};

const PhcPricingSection = ({ plans = [], onUnlock }) => {
  const plan = useMemo(() => resolvePlan(plans), [plans]);

  return (
    <>
      {/* Desktop header handled by parent; card layout */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          width: "100%",
        }}
      >
        <PhcPricingCard plan={plan} onUnlock={onUnlock} />
      </Box>

      {/* Mobile — Pricing title + single free card */}
      <Stack spacing={2} sx={{ display: { xs: "flex", md: "none" }, width: "100%" }}>
        <Text
          sx={{
            fontSize: 18,
            fontWeight: 700,
            color: "#0F172A",
            lineHeight: "24px",
            px: 1,
          }}
        >
          Pricing
        </Text>
        <PhcPricingCard plan={plan} onUnlock={onUnlock} />
      </Stack>
    </>
  );
};

const PhcPricingCard = ({ plan, onUnlock }) => (
  <Box
    sx={{
      width: "100%",
      maxWidth: { xs: "100%", md: 522 },
      p: 2.5,
      borderRadius: "20px",
      bgcolor: "#fff",
      border: "1px solid #E2E8F0",
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack
      spacing={4}
      sx={{
        p: 2.5,
        borderRadius: "16px",
        background: CARD_GRADIENT,
        boxShadow: "0 10px 12px rgba(29,78,216,0.13)",
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ px: 2, py: 0.5, borderRadius: "14px", bgcolor: "#fff", alignSelf: "flex-start" }}>
          <Text sx={{ fontSize: 12, fontWeight: 500, color: BADGE_TEXT }}>
            {plan.badgeLabel}
          </Text>
        </Box>

        <Text sx={{ fontSize: 28, fontWeight: 700, color: "#fff", lineHeight: "21px" }}>
          {formatPriceLabel(plan.planPrice)}
        </Text>

        <Button
          onClick={() => onUnlock?.()}
          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 20 }} />}
          sx={{
            width: "100%",
            height: 42,
            borderRadius: "14px",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 16,
            color: CTA_TEXT,
            bgcolor: "#fff",
            boxShadow: "0 10px 12px rgba(227,99,99,0.2)",
            "&:hover": { bgcolor: "#fff" },
          }}
        >
          {plan.ctaLabel}
        </Button>
      </Stack>

      <Stack spacing={1.25}>
        {plan.benefits.map((benefit) => (
          <Stack key={benefit} direction="row" spacing={1.25} alignItems="flex-start">
            <Box
              sx={{
                width: 24,
                height: 24,
                flexShrink: 0,
                borderRadius: "50%",
                bgcolor: "#fff",
                display: "grid",
                placeItems: "center",
                mt: "1px",
              }}
            >
              <CheckRoundedIcon sx={{ fontSize: 16, color: CTA_TEXT }} />
            </Box>
            <Text sx={{ fontSize: 14, color: "#fff", lineHeight: "21px" }}>{benefit}</Text>
          </Stack>
        ))}
      </Stack>
    </Stack>
  </Box>
);

export default PhcPricingSection;
